import { FloatingPortal } from '@floating-ui/react';
import {
  autoUpdate,
  flip,
  offset,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import clsx from 'clsx';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from 'headlessui2';
import { useId, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { useTheme } from '#ui/hooks/useTheme';
import Icon from '../Icon';
import Tooltip from '../Tooltip';

type Option<TValue> = {
  label: string;
  description?: string | React.ReactNode;
  value: TValue;
  disabled?: boolean;
};

type InputComboboxBaseProps<TValue = string | number> = {
  name: string;
  label: string;
  labelDescription?: string | React.ReactNode;
  labelClassName?: string;
  tooltip?: string;
  placeholder?: string;
  options?: Option<TValue>[];
  optionsClassName?: string;
  disabled?: boolean;
  required?: boolean | string;
  deps?: string | string[];
  optionsMaxHeight?: number;
  className?: string;
};

type InputComboboxSingleProps<TValue = string | number> =
  InputComboboxBaseProps<TValue> & {
    multiple?: false;
    defaultValue?: TValue;
    onChange?: (option: Option<TValue> | undefined) => void;
  };

type InputComboboxMultipleProps<TValue = string | number> =
  InputComboboxBaseProps<TValue> & {
    multiple: true;
    choiceLimit?: number;
    defaultValue?: TValue[];
    onChange?: (options: Option<TValue>[] | undefined) => void;
  };

export type InputComboboxProps<TValue = string | number> =
  | InputComboboxSingleProps<TValue>
  | InputComboboxMultipleProps<TValue>;

export function InputCombobox<T>(props: InputComboboxProps<T>) {
  const {
    name,
    label,
    labelDescription,
    labelClassName,
    tooltip,
    placeholder,
    options,
    optionsClassName,
    required,
    deps,
    defaultValue,
    optionsMaxHeight,
    className,
  } = props;
  const isMultiple = props.multiple === true;
  const choiceLimit = isMultiple ? props.choiceLimit : undefined;
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const { refs, floatingStyles } = useFloating({
    middleware: [
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
      offset(({ placement }) => {
        /* Combobox.Options has margin top 8, so we need to offset it by 16, when the placement is top,
        to have consistent spacing between Combobox.Button and Combobox.Options */
        if (placement === 'top') return 16;
        return 0;
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const filteredOptions = options?.filter((option) =>
    option.label.toLowerCase().includes(query.toLowerCase()),
  );

  const resolvedDefaultValue = isMultiple
    ? Array.isArray(defaultValue)
      ? defaultValue
      : defaultValue !== undefined
        ? [defaultValue]
        : []
    : (defaultValue ?? '');

  const methods = useController({
    name,
    defaultValue: resolvedDefaultValue,
    rules: {
      required,
      deps,
    },
  });

  const { field, fieldState } = methods;

  const disabled = field.disabled || props.disabled;
  const error = disabled ? null : fieldState.error;

  const inputId = useId();
  const errorId = `${inputId}-error`;
  const coerceValues = (value: unknown): T[] =>
    Array.isArray(value) ? (value as T[]) : [];
  const selectedValues = isMultiple ? coerceValues(field.value) : [];
  const selectedValue = isMultiple ? undefined : (field.value as T | undefined);

  const getOptionsFromValues = (values: readonly T[]) => {
    if (!options?.length) return [];
    return values
      .map((value) => options.find((option) => option.value === value))
      .filter((option): option is Option<T> => !!option);
  };

  const selectedOptions = isMultiple
    ? getOptionsFromValues(selectedValues)
    : [];

  return (
    <div className={className}>
      <Combobox
        as={'div'}
        className={clsx('relative', 'kunde:flex kunde:gap-y-3 kunde:flex-col')}
        value={isMultiple ? selectedValues : selectedValue}
        onChange={(value) => {
          if (props.multiple) {
            const nextValues = coerceValues(value);
            if (choiceLimit && nextValues.length > choiceLimit) {
              return;
            }
            field.onChange(nextValues);
            setQuery('');
            if (props.onChange) {
              props.onChange(getOptionsFromValues(nextValues));
            }

            // Close dropdown if choice limit is reached
            if (choiceLimit && nextValues.length >= choiceLimit) {
              closeButtonRef.current?.click();
            }
            return;
          }

          field.onChange(value);
          const option = options?.find((option) => option.value === value);
          setQuery('');
          if (props.onChange) {
            props.onChange(option);
          }
        }}
        disabled={disabled}
        onBlur={field.onBlur}
        onBlurCapture={field.onBlur}
        immediate
        multiple={isMultiple}
      >
        {({ open }) => (
          <>
            <Tooltip tooltip={tooltip} enabled={!!tooltip}>
              <div>
                <label
                  htmlFor={inputId}
                  className={twMerge('laane:mb-1 block', labelClassName)}
                >
                  {label}
                </label>
                {labelDescription ? (
                  <div
                    className={clsx(
                      'laane:contents kunde:text-neutral-100 kunde:text-2xs',
                    )}
                  >
                    {labelDescription}
                  </div>
                ) : null}
              </div>
            </Tooltip>
            <div
              className={clsx('group relative flex items-center', {
                'flex-wrap gap-2': isMultiple,
              })}
              ref={isMultiple ? refs.setReference : undefined}
            >
              {isMultiple ? (
                <div
                  className={clsx(
                    'flex min-h-10 w-full flex-wrap items-center gap-2 pl-3 pr-10 py-1',
                    'kunde:pl-4',
                    'border border-neutral-10 bg-neutral-5 laane:rounded kunde:rounded-md',
                    'typ-ui kunde:typ-body transition-colors',
                    'kunde:border-neutral-40 kunde:text-sm',
                    {
                      'hover:border-neutral-40 hover:bg-neutral-10 focus-within:border-blue-20 focus-within:bg-white focus-within:ring-1 focus-within:ring-blue-20':
                        !disabled,
                      'kunde:focus-within:ring-2 kunde:focus-within:ring-blue-60':
                        !disabled,
                      'cursor-not-allowed border-neutral-40 bg-neutral-10 text-neutral-100':
                        disabled,
                      'kunde:text-neutral-60': disabled,
                    },
                  )}
                >
                  {selectedOptions.map((option) => (
                    <button
                      key={String(option.value)}
                      type="button"
                      className={clsx(
                        'inline-flex h-5 items-center gap-2 rounded-lg bg-blue-100 px-2 text-white',
                        'laane:typ-ui-sm kunde:typ-body-sm',
                        {
                          'cursor-not-allowed bg-neutral-40 text-neutral-0':
                            disabled,
                        },
                      )}
                      aria-label={`Remove ${option.label}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (disabled) return;
                        const nextValues = (selectedValues as T[]).filter(
                          (value) => value !== option.value,
                        );
                        field.onChange(nextValues);
                        if (props.multiple && props.onChange) {
                          props.onChange(getOptionsFromValues(nextValues));
                        }
                      }}
                    >
                      <span className="truncate">{option.label}</span>
                      <Icon type="cross" size="tiny" className="size-3" />
                    </button>
                  ))}
                  <ComboboxInput
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (disabled) return;
                      if (event.key !== 'Backspace') return;
                      if (query !== '') return;
                      if (selectedValues.length === 0) return;

                      event.preventDefault();
                      const nextValues = selectedValues.slice(0, -1);
                      field.onChange(nextValues);
                      if (props.multiple && props.onChange) {
                        props.onChange(getOptionsFromValues(nextValues));
                      }
                    }}
                    value={query}
                    className={clsx(
                      'flex-1 min-w-24 !border-0 !bg-transparent !p-0',
                      'focus:!border-0 focus:!ring-0',
                      'enabled:hover:!border-0 enabled:hover:!bg-transparent',
                      'disabled:!bg-transparent',
                    )}
                    aria-describedby={error ? errorId : undefined}
                    aria-invalid={!!error}
                    displayValue={() => ''}
                    placeholder={
                      selectedOptions.length > 0 ? '' : (placeholder ?? '')
                    }
                    name={name}
                    id={inputId}
                  />
                </div>
              ) : (
                <ComboboxInput
                  onChange={(event) => setQuery(event.target.value)}
                  className="group-hover:bg-neutral-10 w-full kunde:h-10"
                  aria-describedby={error ? errorId : undefined}
                  aria-invalid={!!error}
                  displayValue={(value) =>
                    options?.filter((option) => option.value === value)[0]
                      ?.label ?? ''
                  }
                  placeholder={placeholder}
                  name={name}
                  ref={refs.setReference}
                  id={inputId}
                />
              )}
              <ComboboxButton
                ref={closeButtonRef}
                className="absolute right-2 flex items-center !border-none !bg-transparent"
                aria-label="Toggle options"
              >
                <Icon
                  type="chevron-down"
                  className={clsx('text-neutral-110 ', {
                    'rotate-180': open,
                  })}
                />
              </ComboboxButton>
            </div>
            {filteredOptions?.length ? (
              <FloatingPortal>
                <ComboboxOptions
                  as="ul"
                  className={twMerge(
                    clsx(
                      'bg-neutral-0 absolute z-[120] mt-2 w-fit overflow-y-scroll py-1 shadow-lg focus:ring-0',
                      'laane:rounded',
                      'kunde:rounded-lg kunde:mt-2 kunde:shadow-lg-dl kunde:p-2',
                    ),
                    optionsClassName,
                  )}
                  style={{
                    ...floatingStyles,
                    maxHeight: `${optionsMaxHeight}px`,
                  }}
                  ref={refs.setFloating}
                >
                  {filteredOptions.map((option, i) => {
                    const isOptionSelected = isMultiple
                      ? selectedValues.includes(option.value as T)
                      : false;
                    const disableDueToLimit =
                      isMultiple &&
                      !!choiceLimit &&
                      selectedValues.length >= choiceLimit &&
                      !isOptionSelected;

                    return (
                      <ComboboxOption
                        as="li"
                        key={i}
                        value={option.value}
                        disabled={option.disabled || disableDueToLimit}
                        aria-labelledby={`option-${option.value}-label`}
                        aria-describedby={
                          option.description
                            ? `option-${option.value}-description`
                            : undefined
                        }
                      >
                        {({ active, selected }) => (
                          <div
                            className={clsx(
                              'laane:typ-ui laane: hover:bg-neutral-5 laane:active:bg-neutral-5 select-none laane:px-4 laane:py-1.5',
                              'kunde:p-3 kunde:typ-body-sm kunde:rounded-sm kunde:flex kunde:items-center',
                              {
                                'text-neutral-60 cursor-default':
                                  option.disabled || disableDueToLimit,
                                'cursor-pointer':
                                  !option.disabled && !disableDueToLimit,
                                'laane:bg-neutral-5 kunde:bg-blue-5 kunde:text-blue-60':
                                  active || selected,
                              },
                            )}
                          >
                            <div
                              className={clsx('flex items-start gap-3', {
                                'text-neutral-60': option.disabled,
                                'items-center': isMultiple,
                              })}
                            >
                              {isMultiple ? (
                                <span
                                  className={clsx(
                                    'checkbox pointer-events-none',
                                    {
                                      checked: selected,
                                    },
                                  )}
                                  aria-hidden="true"
                                />
                              ) : null}
                              <div className="flex flex-col">
                                <div id={`option-${option.value}-label`}>
                                  {option.label}
                                </div>
                                {option.description ? (
                                  <div
                                    className="text-neutral-100 typ-ui-sm w-auto leading-2"
                                    id={`option-${option.value}-description`}
                                  >
                                    {option.description}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            {selected && theme === 'kunde' && !isMultiple ? (
                              <>
                                <span className="flex-1" />
                                <Icon
                                  type="check-circle"
                                  className="laane:text-blue-100 kunde:text-blue-60 kunde:min-w-6 kunde:min-h-6"
                                  solid
                                  size={'small'}
                                />
                              </>
                            ) : null}
                          </div>
                        )}
                      </ComboboxOption>
                    );
                  })}
                </ComboboxOptions>
              </FloatingPortal>
            ) : null}
          </>
        )}
      </Combobox>
      {error ? (
        <div
          id={errorId}
          className="typ-headline-2xs mt-2 flex items-start justify-start space-x-1 text-red-100"
        >
          <Icon type="alert-diamond" solid size="tiny" />
          <span className="flex-1">{error.message}</span>
        </div>
      ) : null}
    </div>
  );
}

export default InputCombobox;
